import Script from "next/script"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { RegionConfiguration } from "modules/i18n/utils/loadRegionConfiguration"

const DIDOMI_EMBED_ID = "a1179046-1792-42d9-812f-3daba6c97289"

/**
 * Check if Didomi is enabled for the environment and the region configuration.
 * @param regionConfiguration
 */
export const isEnabled = (regionConfiguration: RegionConfiguration) => {
  const isEnvEnabled = process.env.NEXT_PUBLIC_DIDOMI_ENABLED == "true"
  const isRegionEnabled = Boolean(regionConfiguration?.didomi?.enabled)

  return isEnvEnabled && isRegionEnabled
}

/**
 * Returns Didomi's custom `<script>` tag type `didomi/javascript` attribute if it's enabled, otherwise, it'll return
 * `text/javascript`
 */
export const scriptType = (regionConfiguration: RegionConfiguration): string =>
  isEnabled(regionConfiguration) ? "didomi/javascript" : "text/javascript"

/**
 * Didomi setup component. It exposes the `window.didomiOnReady` global object so we can safely invoke it
 * even if the environment does not have Didomi enabled.
 */
export const Didomi = () => {
  const regionConfig = useRegionConfig()

  if (!isEnabled(regionConfig)) {
    return (
      <Script
        data-testid="didomi-snippet"
        id="didomi-snippet"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            window.didomiOnReady = window.didomiOnReady || [];
          `,
        }}
      />
    )
  }

  return (
    <Script
      data-testid="didomi-snippet"
      id="didomi-snippet"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          window.didomiOnReady = window.didomiOnReady || [];
          window.didomiEventListeners = window.didomiEventListeners || [];
          window.gdprAppliesGlobally=true;(function(){function a(e){if(!window.frames[e]){if(document.body&&document.body.firstChild){var t=document.body;var n=document.createElement("iframe");n.style.display="none";n.name=e;n.title=e;t.insertBefore(n,t.firstChild)}
  else{setTimeout(function(){a(e)},5)}}}function e(n,r,o,c,s){function e(e,t,n,a){if(typeof n!=="function"){return}if(!window[r]){window[r]=[]}var i=false;if(s){i=s(e,t,n)}if(!i){window[r].push({command:e,parameter:t,callback:n,version:a})}}e.stub=true;function t(a){if(!window[n]||window[n].stub!==true){return}if(!a.data){return}
  var i=typeof a.data==="string";var e;try{e=i?JSON.parse(a.data):a.data}catch(t){return}if(e[o]){var r=e[o];window[n](r.command,r.parameter,function(e,t){var n={};n[c]={returnValue:e,success:t,callId:r.callId};a.source.postMessage(i?JSON.stringify(n):n,"*")},r.version)}}
  if(typeof window[n]!=="function"){window[n]=e;if(window.addEventListener){window.addEventListener("message",t,false)}else{window.attachEvent("onmessage",t)}}}e("__tcfapi","__tcfapiBuffer","__tcfapiCall","__tcfapiReturn");a("__tcfapiLocator");(function(e){
    var t=document.createElement("script");t.id="spcloader";t.type="text/javascript";t.async=true;t.src="https://sdk.privacy-center.org/"+e+"/loader.js?target="+document.location.hostname;t.charset="utf-8";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n)})("${DIDOMI_EMBED_ID}")})();
        `,
      }}
    />
  )
}
